<template>
  <div>
    <NavbarTwo />

    <div class="cu-hero">
      <div class="container mw-1356">
        <span class="cu-top-label">CONTACT US</span>
        <h1>{{ c.heading || 'Get In Touch' }}</h1>
        <p>{{ c.subheading || "We're here to help — reach out any time" }}</p>
      </div>
    </div>

    <div class="cu-body bg-gondola">
      <div class="container mw-1356" style="max-width:860px">

        <!-- Info cards row -->
        <div class="cu-cards">
          <a v-if="c.email" :href="'mailto:' + c.email" class="cu-card">
            <div class="cu-card-icon"><i class="ti ti-mail"></i></div>
            <div class="cu-card-label">Email</div>
            <div class="cu-card-val">{{ c.email }}</div>
          </a>
          <a v-if="c.phone" :href="'tel:' + c.phone" class="cu-card">
            <div class="cu-card-icon"><i class="ti ti-phone"></i></div>
            <div class="cu-card-label">Phone / WhatsApp</div>
            <div class="cu-card-val">{{ c.phone }}</div>
          </a>
          <a v-if="c.telegram" :href="c.telegram" target="_blank" rel="noopener" class="cu-card">
            <div class="cu-card-icon"><i class="ti ti-brand-telegram"></i></div>
            <div class="cu-card-label">Telegram</div>
            <div class="cu-card-val">{{ c.telegram }}</div>
          </a>
          <div v-if="c.hours" class="cu-card">
            <div class="cu-card-icon"><i class="ti ti-clock"></i></div>
            <div class="cu-card-label">Support Hours</div>
            <div class="cu-card-val">{{ c.hours }}</div>
          </div>
          <div v-if="c.address" class="cu-card">
            <div class="cu-card-icon"><i class="ti ti-map-pin"></i></div>
            <div class="cu-card-label">Address</div>
            <div class="cu-card-val">{{ c.address }}</div>
          </div>
        </div>

        <!-- Message form -->
        <div class="cu-form-box">
          <h3 class="cu-form-title">Send a Message</h3>

          <div v-if="formSent" class="cu-success">
            <i class="ti ti-circle-check"></i> Message sent! We'll get back to you soon.
          </div>

          <form v-else @submit.prevent="submitForm">
            <div v-if="formError" class="cu-error">{{ formError }}</div>

            <div class="cu-row">
              <div class="cu-field">
                <label>Your Name</label>
                <input v-model="form.name" type="text" required placeholder="John Doe" />
              </div>
              <div class="cu-field">
                <label>Email</label>
                <input v-model="form.email" type="email" required placeholder="you@example.com" />
              </div>
            </div>

            <div class="cu-field" style="margin-top:1rem">
              <label>Phone Number <span style="color:#666;font-size:0.75rem">(optional — so we can call you)</span></label>
              <input v-model="form.phone" type="tel" placeholder="+1 234 567 8900" />
            </div>

            <div class="cu-field" style="margin-top:1rem">
              <label>Message</label>
              <textarea v-model="form.message" required rows="5" placeholder="How can we help?"></textarea>
            </div>

            <button type="submit" class="btn cu-submit" :disabled="formLoading">
              <span class="border-style">{{ formLoading ? 'Sending...' : 'Send Message' }}</span>
            </button>
          </form>
        </div>

      </div>
    </div>

    <MainFooter />
    <CopyRight />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, reactive } from "vue";
import { useSeo } from "@/composables/useSeo";
import NavbarTwo from "@/components/Layouts/NavbarTwo.vue";
import MainFooter from "@/components/Layouts/MainFooter.vue";
import CopyRight from "@/components/Layouts/CopyRight.vue";
import { useContent } from "@/composables/useContent";
import { useRecaptcha } from "@/composables/useRecaptcha";
import api from "@/services/api";

export default defineComponent({
  name: "ContactPage",
  components: { NavbarTwo, MainFooter, CopyRight },
  setup() {
    useSeo({
      title: "Contact Us",
      description: "Get in touch with goplayTv support. We're available 24/7 to help with your IPTV subscription, setup, and World Cup 2026 streaming questions.",
      url: "/contact-us",
      keywords: "goplayTv contact, IPTV support, customer service, help with IPTV",
    });
    const { content } = useContent("contact", {
      heading: "", subheading: "", email: "", phone: "", telegram: "", address: "", hours: "",
    });
    const c = computed(() => content.value);
    const { getToken } = useRecaptcha();

    const form = reactive({ name: "", email: "", phone: "", message: "" });
    const formLoading = ref(false);
    const formSent = ref(false);
    const formError = ref("");

    async function submitForm() {
      formLoading.value = true;
      formError.value = "";
      try {
        const recaptcha_token = await getToken("contact");
        await api.post("/api/v1/contact", { ...form, recaptcha_token });
        formSent.value = true;
      } catch (e: any) {
        formError.value = e?.response?.data?.message || "Failed to send message. Please try again.";
      } finally {
        formLoading.value = false;
      }
    }

    return { c, form, formLoading, formSent, formError, submitForm };
  },
});
</script>

<style scoped>
/* Hero */
.cu-hero {
  background: linear-gradient(135deg, #0a0102 0%, #1a0508 100%);
  padding: 100px 0 60px;
  border-bottom: 1px solid #2b1e1f;
  text-align: center;
}
.cu-top-label {
  display: inline-block;
  color: #c90e0e;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 1rem;
}
.cu-hero h1 { color: #fff; font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 800; margin-bottom: 0.75rem; }
.cu-hero p  { color: #a0a0a0; }

/* Body */
.cu-body { padding: 60px 0 80px; }

/* Info cards */
.cu-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2.5rem;
}
.cu-card {
  flex: 1 1 180px;
  background: #1a0f10;
  border: 1px solid #2b1e1f;
  border-radius: 12px;
  padding: 1.25rem;
  text-align: center;
  text-decoration: none;
  transition: border-color 0.2s, transform 0.2s;
  display: block;
}
.cu-card:hover { border-color: #c90e0e; transform: translateY(-2px); }
.cu-card-icon {
  width: 44px; height: 44px;
  border-radius: 50%;
  background: rgba(201,14,14,0.12);
  display: flex; align-items: center; justify-content: center;
  color: #c90e0e;
  font-size: 1.2rem;
  margin: 0 auto 0.75rem;
}
.cu-card-label { color: #666; font-size: 0.72rem; margin-bottom: 0.3rem; text-transform: uppercase; letter-spacing: 1px; }
.cu-card-val   { color: #fff; font-size: 0.88rem; font-weight: 500; word-break: break-word; }

/* Form box */
.cu-form-box {
  background: #1a0f10;
  border: 1px solid #2b1e1f;
  border-radius: 16px;
  padding: 2rem;
}
.cu-form-title {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.cu-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.cu-row .cu-field { flex: 1 1 200px; }

.cu-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.cu-field label { color: #a0a0a0; font-size: 0.8rem; }
.cu-field input,
.cu-field textarea {
  background: #0a0102;
  border: 1px solid #3a2526;
  border-radius: 8px;
  color: #fff;
  padding: 0.65rem 0.95rem;
  font-size: 0.9rem;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: border-color 0.2s;
  width: 100%;
}
.cu-field input:focus,
.cu-field textarea:focus { border-color: #c90e0e; }

.cu-submit { width: 100%; margin-top: 1.25rem; }

.cu-success {
  background: rgba(25,135,84,0.12);
  border: 1px solid rgba(25,135,84,0.3);
  color: #4ade80;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.9rem;
}
.cu-error {
  background: rgba(201,14,14,0.12);
  border: 1px solid rgba(201,14,14,0.3);
  color: #f87171;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  font-size: 0.82rem;
  margin-bottom: 1rem;
}
</style>
